import { ICountryAsset, ICountryAssetLite } from "models/Assets/ICountryAsset";
import {
  ICategoryAsset,
  ICategoryLiteAsset,
  ICategoryLiteWithChildsAsset,
  ICustomCategoryAsset,
  IVirtualCatAsset,
  IVirtualCatLiteAsset,
} from "models/Assets/ICategoryAsset";
import { ICityAsset, ICityLiteAsset } from "models/Assets/ICityAsset";
import { NextPageContextEnhanced } from "../../types/page";
import {
  toLiteCategories,
  toLiteCategory,
  toLiteCatsWithChilds,
} from "dataLayer/typesMappers/toLiteCategory";
import { toLiteCities } from "dataLayer/typesMappers/toLiteCity";
import {
  toLiteCountries,
  toLiteCountry,
} from "dataLayer/typesMappers/toLiteCountry";

import {
  ICPAttributesCollection,
  ICpOption,
  IDynamicAttributeEnhanced,
  IDynamicRangeAttribute,
  IDynamicRangeAttributes,
} from "models/Assets/IDynamicAttributes";
import { toLiteNHood } from "dataLayer/typesMappers/toLiteNHood";
import {
  INeighborhoodAsset,
  INeighborhoodLiteAsset,
} from "models/Assets/INeighborhoodAsset";
import filterSubCategoriesByMainCategory from "dataLayer/filters/filterSubCategoriesByMainCategory";
import {
  filterCityByCityId,
  filterCityByUrlName,
  filterNeighborhoodByNeighborhoodUrlName,
  filterNeighborhoodFromCitiesByNeighborhoodId,
} from "dataLayer/filters/citiesAndHoods";
import {
  filterDynamicAttributesBySubCatId,
  toLiteCPAttribute,
} from "dataLayer/filters/filterDynamicAttributes";
import {
  DynamicRangeAttributeQueryString,
  SelectableCPsQueryString,
} from "contexts/RoutesObjectContext";
import {
  filterMainCategoryBySubCatId,
  filterSubCategoriesBySubCatId,
} from "dataLayer/filters/categories";
import { toLiteVirtualCats } from "dataLayer/typesMappers/toLiteVirtualCat";
import { IMegaMenu } from "models/megaMenu";
import { getCategoryByCustomBaseName } from "dataLayer/apisWithCache/assetsApis/categories";
import { TypeAllowedCustomLandingBaseNames } from "componentScreens/LandingPages";
import { EnumScreenNames } from "../../enums/EnumScreenNames";

interface TypeBasicsEssentialData {
  countries: ICountryAssetLite[];
  cities: ICityLiteAsset[];
  categories: ICategoryLiteAsset[];
}

class EssentialData {
  private readonly _context: NextPageContextEnhanced;
  private _countries: ICountryAsset[];

  private _categories: ICategoryAsset[];
  private _cities: ICityAsset[];
  private _dynamicAttrs: ICPAttributesCollection;
  private _dynamicAttrsWithUntranslatedOptions: ICPAttributesCollection;
  private _virtualCategories: IVirtualCatAsset[];
  private _megaMenu: IMegaMenu;
  private _translations: any = {};
  static _instances = {};

  static getInstance(context: NextPageContextEnhanced): EssentialData {
    let { lang = null, countryCode = null } = context || {};
    lang =
      lang && ["en", "ar"].includes(lang) ? lang : process.env.DEFAULT_LANG;

    countryCode =
      countryCode && process.env.COUNTRY_CODES.split("|").includes(countryCode)
        ? countryCode
        : "xx";

    if (!EssentialData._instances?.[lang]?.[countryCode]) {
      EssentialData._instances[lang] = EssentialData._instances[lang] || {};
      EssentialData._instances[lang][countryCode] =
        EssentialData._instances[lang][countryCode] || {};
      EssentialData._instances[lang][countryCode] = new EssentialData(context);
    }
    EssentialData._instances[lang][countryCode]._context = context;
    return EssentialData._instances[lang][countryCode];
  }

  private constructor(context) {
    this._context = context;
  }

  public setAllEssentialData(allEssentialData) {
    this._countries = allEssentialData.countries;
    this._cities = allEssentialData.cities;
    this._categories = allEssentialData.categories;
    this._dynamicAttrs = allEssentialData.dynamicAttrs;
    this._dynamicAttrsWithUntranslatedOptions =
      allEssentialData.dynamicAttrsWithUntranslatedOptions;
    this._virtualCategories = allEssentialData.virtualCategories;
    this._megaMenu = allEssentialData.megaMenu;
  }

  public setAllTranslations(translations): void {
    this._translations = translations;
  }
  public getCategories_ServerOnly(): ICategoryAsset[] {
    return this._categories;
  }

  public getCities_ServerOnly(): ICityAsset[] {
    return this._cities;
  }

  public getCountries_ServerOnly(): ICountryAsset[] {
    return this._countries;
  }

  public getLiteCountries(): ICountryAssetLite[] {
    return toLiteCountries(this._context.lang)(this._countries);
  }

  public getCurrentCountry(): ICountryAssetLite {
    return toLiteCountry(this._context.lang)(
      this._countries.find(
        (country) => country.abbreviation == this._context?.countryCode
      )
    );
  }

  public getLiteCategories(): ICategoryLiteAsset[] {
    return toLiteCategories(this._context.lang)(this._categories);
  }

  public getLiteCategoriesWithChilds(): ICategoryLiteWithChildsAsset[] {
    return toLiteCatsWithChilds(this._context.lang)(this._categories);
  }

  public getLiteVirtualCategories(): IVirtualCatLiteAsset[] {
    return toLiteVirtualCats(this._context.lang)(this._virtualCategories);
  }

  public getCategoryByCustomBaseName(
    baseName: TypeAllowedCustomLandingBaseNames
  ): ICustomCategoryAsset {
    return getCategoryByCustomBaseName(baseName);
  }

  public getLiteCites(): ICityLiteAsset[] {
    return toLiteCities(this._context.lang)(this._cities);
  }

  public getCapitalCityById(id: number): ICityLiteAsset {
    const cityIdx = this._cities.findIndex((val) => val.id === id);
    return cityIdx == -1 ? null : this.getLiteCites()[cityIdx];
  }

  public getPageConfig(): any {
    return {
      pageConfig: {
        unstable_runtimeJS:
          process.env.BOTS_MODE !== "1" &&
          this._context?.query?.unstable_runtimeJS !== "1",
      },
    };
  }

  public getBasics(): TypeBasicsEssentialData {
    return {
      ...this.getPageConfig(),
      countries: [this.getCurrentCountry()],
      cities: this.getLiteCites(),
      categories: this.getLiteCategories(),
    };
  }

  public getCommonTranslation(): any {
    return this._translations.common || {};
  }

  public getScreenTranslations(screen: EnumScreenNames): any {
    return this._translations.screens[screen];
  }

  public getCityById(cityId: string | number): ICityAsset {
    return filterCityByCityId<ICityAsset>(this._cities, cityId);
  }

  public findNHoodByCityAndNhoodId(
    city: ICityAsset,
    nHoodId: string | number,
    lang: string
  ): INeighborhoodLiteAsset | null {
    if (!city.pwaNeighborhoods) return null;
    if (!nHoodId) return null;
    return toLiteNHood(
      city.pwaNeighborhoods.find((n) => n.id == nHoodId),
      lang
    );
  }

  public getCityByUrlName(urlName: string): ICityAsset {
    return filterCityByUrlName<ICityAsset>(this._cities, urlName);
  }

  public getMainCategoryById<T = ICategoryAsset>(
    categoryId: string | number,
    isLite = false
  ): ICategoryAsset | ICategoryLiteAsset {
    if (!categoryId) return null;
    const [category = null] = this._categories.filter(
      (c) => c.id == categoryId
    );
    if (isLite) {
      return toLiteCategory(this._context.lang)(category);
    } else {
      return category;
    }
  }

  public getMainCategoryByReportingName(
    reporting_name: string
  ): ICategoryAsset {
    if (!reporting_name) return null;
    const [category] = this._categories.filter(
      (c) => c.reporting_name == reporting_name
    );
    return category || null;
  }

  public getMainCategoryByUrlName(url_name: string): ICategoryAsset {
    if (!url_name) return null;
    const [category] = this._categories.filter(
      (c) => c.url_name_ar == url_name || c.url_name_en == url_name
    );
    return category || null;
  }

  public getSubCategoryByUrlName(
    category: ICategoryAsset,
    url_name: string
  ): ICategoryAsset {
    if (!url_name) return null;
    const [subCategory = null] = category.subCategories.filter(
      (c) => c.url_name_ar == url_name || c.url_name_en == url_name
    );
    return subCategory;
  }

  public getSubCategoryByReportingName(
    category: ICategoryAsset,
    reporting_name: string
  ): ICategoryAsset {
    if (!reporting_name) return null;
    const [subCategory = null] = category.subCategories.filter(
      (c) => c.reporting_name == reporting_name
    );
    return subCategory;
  }

  public getLiteNHoodsByCity(city: ICityAsset): INeighborhoodLiteAsset[] {
    return (city?.pwaNeighborhoods || []).map((nHood) =>
      toLiteNHood(nHood, this._context.lang)
    );
  }

  public getMainCategoryBySubCatId(
    subCatId: string | number
  ): ICategoryLiteAsset {
    return toLiteCategory(this._context.lang)(
      filterMainCategoryBySubCatId(this._categories, subCatId)
    );
  }

  public getSubCategoryBySubCatId(
    subCatId: string | number,
    returnFullData: boolean = false
  ): ICategoryLiteAsset {
    if (returnFullData) {
      return filterSubCategoriesBySubCatId(this._categories, subCatId);
    }
    return toLiteCategory(this._context.lang)(
      filterSubCategoriesBySubCatId(this._categories, subCatId)
    );
  }

  public getOptionById(
    subCategory: ICategoryAsset,
    optionId: number,
    parentId?: number
  ): ICpOption | null {
    //const attrs = this.getDynamicAttributesBySubCatId(subCategory.id);
    const attrs = this._dynamicAttrsWithUntranslatedOptions[subCategory.id];

    //@todo
    //to Be Fixed
    //to be optimized

    //Object.keys(attrsByFieldName).forEach((fielName) => {
    let found: ICpOption = null;
    attrs.forEach((attr) => {
      //const attr = attrsByFieldName[fielName];
      if (found) return;
      //attr.options
      [found = null] = (attr.options || []).filter(
        (option) => option.id == optionId
      );
      if (!found) {
        if (parentId) {
          [found = null] = (attr.optionsByParents[parentId] || []).filter(
            (option) => option.id == optionId
          );
        }
      }
    });

    return found;
  }

  public getOptionByUrlName(
    subCategoryId: number,
    url: string,
    parentId?: number
  ): ICpOption | null {
    //const attrs = this.getDynamicAttributesBySubCatId(subCategory.id);
    const attrs = this._dynamicAttrsWithUntranslatedOptions[subCategoryId];

    //@todo
    //to Be Fixed
    //to be optimized

    //Object.keys(attrsByFieldName).forEach((fielName) => {
    let found: ICpOption = null;
    attrs.forEach((attr) => {
      //const attr = attrsByFieldName[fielName];
      if (found) return;
      //attr.options
      [found = null] = (attr.options || []).filter((option) =>
        [option.url_name_ar, option.url_name_en].includes(url)
      );
      if (!found) {
        if (parentId) {
          [found = null] = (attr.optionsByParents[parentId] || []).filter(
            (option) => [option.url_name_ar, option.url_name_en].includes(url)
          );
        }
      }
    });

    return found;
  }

  public getSubCategoriesByMainCategory(
    categoryId: string | number
  ): ICategoryLiteAsset[] {
    return filterSubCategoriesByMainCategory(this._categories)(categoryId).map(
      toLiteCategory(this._context.lang)
    );
  }

  public getDynamicAttributesBySubCatId(
    subCatId: string | number,
    selectedCPIds?: SelectableCPsQueryString
  ): IDynamicAttributeEnhanced[] {
    const dynamicAttributesEnhanced =
      filterDynamicAttributesBySubCatId(this._dynamicAttrs, subCatId) || [];
    return dynamicAttributesEnhanced.map((attr) => {
      const [parentFieldName] = attr.fieldName.split("_child");
      return toLiteCPAttribute(
        this._context.lang,
        attr,
        selectedCPIds?.[parentFieldName]
      );
    });
  }

  public getSelectedCpOptionsByDynamicRouteObjectIds(
    subCategoryId: string | number,
    selectedCPIds?: SelectableCPsQueryString
  ): ICpOption[] {
    if (!subCategoryId || !selectedCPIds) return [];

    const dynamicAttributesEnhanced =
      filterDynamicAttributesBySubCatId(this._dynamicAttrs, subCategoryId) ||
      [];
    const attrsByFieldNames: { [key: string]: IDynamicAttributeEnhanced } = {};
    dynamicAttributesEnhanced.forEach((attr) => {
      const [parentFieldName] = attr.fieldName.split("_child");
      attrsByFieldNames[attr.fieldName] = toLiteCPAttribute(
        this._context.lang,
        attr,
        selectedCPIds?.[parentFieldName]
      );
    });
    let cpOptions: ICpOption[] = [];
    Object.keys(selectedCPIds).forEach((fieldName) => {
      if (
        Array.isArray(selectedCPIds[fieldName]) &&
        attrsByFieldNames[fieldName]
      ) {
        selectedCPIds[fieldName].forEach((cpOptionId) => {
          cpOptions.push(
            attrsByFieldNames[fieldName]?.options.find(
              (o) => o.id == cpOptionId || o.value == cpOptionId
            )
          );
        });
      }
    });

    return cpOptions.filter(Boolean);
  }

  public getRangeDynamicAttributes(
    subCategoryId: string | number,
    rangeAttrsQs?: DynamicRangeAttributeQueryString
  ): IDynamicRangeAttributes {
    if (!subCategoryId) return {};

    const output: IDynamicRangeAttributes = {};

    const dynamicAttributesEnhanced =
      filterDynamicAttributesBySubCatId(this._dynamicAttrs, subCategoryId) ||
      [];

    dynamicAttributesEnhanced.forEach((attr) => {
      if (!rangeAttrsQs[attr.fieldName]) return;
      if (
        rangeAttrsQs[attr.fieldName]?.from ||
        rangeAttrsQs[attr.fieldName]?.to
      ) {
        const [unit = null] = (attr.field.units || []).filter(
          (u) => u.id === Number(rangeAttrsQs[attr.fieldName].unit)
        );
        const data: IDynamicRangeAttribute = {
          ...rangeAttrsQs[attr.fieldName],
          unit,
        };
        output[attr.fieldName] = data;
      }
    });

    return output;
  }

  public getFirstDynamicAttributeBySubCatId(
    subCatId: string | number
  ): IDynamicAttributeEnhanced {
    const attrs = this.getDynamicAttributesBySubCatId(subCatId);
    const f = attrs[0];
    if (!f.field.is_linkable) {
      return null;
    }
    return f;
  }

  public getNeighborhoodFromCitiesByNeighborhoodId(
    nHoodId: string | number,
    returnFullData: boolean = false
  ): INeighborhoodLiteAsset | INeighborhoodAsset {
    if (returnFullData) {
      return filterNeighborhoodFromCitiesByNeighborhoodId(
        this._cities,
        nHoodId
      );
    }
    return toLiteNHood(
      filterNeighborhoodFromCitiesByNeighborhoodId(this._cities, nHoodId),
      this._context.lang
    );
  }

  public getNeighborhoodFromCitiesByUrlName(
    city: ICityAsset,
    url: number,
    returnFullData: boolean = false
  ): INeighborhoodLiteAsset | INeighborhoodAsset {
    if (returnFullData) {
      return filterNeighborhoodByNeighborhoodUrlName(
        city.pwaNeighborhoods,
        url
      );
    }
    return toLiteNHood(
      filterNeighborhoodByNeighborhoodUrlName(city.pwaNeighborhoods, url),
      this._context.lang
    );
  }

  public getMegaMenu(): IMegaMenu {
    return this._megaMenu;
  }
}

export default EssentialData;
